import { ActionIcon, Button, Kbd, Loader } from '@mantine/core';
import { useLocalStorage, useOs } from '@mantine/hooks';
import { Spotlight, createSpotlight } from '@mantine/spotlight';
import IconSearch from '@tabler/icons-react/dist/esm/icons/IconSearch.mjs';
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { navSearchProvider } from '../model/providers/nav-search';
import { PAGE_SEARCH_PROVIDERS } from '../model/providers/page-providers';
import {
  completeSearchQuery,
  popSearchQuery,
} from '../model/query/match-query';
import {
  toRecentSearchAction,
  useRecentSearches,
} from '../model/recent/recent-searches';
import { searchRegistry } from '../model/registry';
import { canDrillAction, getCommitHref } from './spotlight/action-intent';
import { navigateFromSpotlight } from './spotlight/spotlight-navigate';
import { SpotlightResults } from './spotlight/spotlight-results';
import {
  resolveSpotlightAction,
  syncSpotlightSelection,
} from './spotlight/spotlight-select';
import type {
  AppSearchAction,
  AppSearchCommitMode,
  AppSearchNavNode,
} from '../model/types';
import { useAppSearchResults } from '../model/use-app-search-results';

const [appSearchStore, appSearch] = createSpotlight();

/** Theme Modal wraps content in ScrollAreaAutosize; that overlay shadow does not belong on Spotlight. */
const SpotlightScrollArea = ({ children }: { children?: ReactNode }) => (
  <>{children}</>
);

const AppSearchButton = () => {
  const { t } = useTranslation('header');
  const os = useOs();
  const shortcut = os === 'macos' || os === 'ios' ? '⌘K' : 'Ctrl+K';

  return (
    <>
      <ActionIcon
        variant='light'
        hiddenFrom='sm'
        onClick={appSearch.open}
        aria-label={t('search.placeholder')}
        data-testid='app-search-button-mobile'
      >
        <IconSearch size={20} />
      </ActionIcon>
      <Button
        variant='default'
        size='xs'
        visibleFrom='sm'
        justify='space-between'
        miw={200}
        fw={400}
        c='dimmed'
        leftSection={<IconSearch size={16} />}
        rightSection={<Kbd size='xs'>{shortcut}</Kbd>}
        onClick={appSearch.open}
        data-testid='app-search-button'
        aria-label={t('search.placeholder')}
      >
        {t('search.placeholder')}
      </Button>
    </>
  );
};

export const AppSearch = memo(
  ({ navigation }: { navigation: AppSearchNavNode[] }) => {
    const { t } = useTranslation('header');
    const { t: tNav, i18n } = useTranslation('navigation');
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [commitMode, setCommitMode] = useLocalStorage<AppSearchCommitMode>({
      key: 'app-search-mode',
      defaultValue: 'select',
    });

    const completeSearch = useCallback((action: AppSearchAction) => {
      setQuery(completeSearchQuery(action));
    }, []);

    const { actions, scopeKey, isLoading } = useAppSearchResults(
      query,
      completeSearch,
      navigation,
    );
    const {
      items: recentItems,
      add: addRecentSearch,
      remove: removeRecentSearch,
    } = useRecentSearches();
    const recentActions = useMemo(() => {
      if (query.trim()) {
        return [];
      }

      return recentItems.map((item) => toRecentSearchAction(item, tNav));
    }, [i18n.language, query, recentItems, tNav]);
    const listedActions = useMemo(
      () => [...recentActions, ...actions],
      [actions, recentActions],
    );
    const listRef = useRef<HTMLDivElement>(null);
    const hoveredIndexRef = useRef<number | null>(null);
    const [ignoreHover, setIgnoreHover] = useState(false);

    const drillAction = useCallback(
      (action: AppSearchAction) => {
        if (canDrillAction(action)) {
          completeSearch(action);
        }
      },
      [completeSearch],
    );

    const commitAction = useCallback(
      (action: AppSearchAction) => {
        const href = getCommitHref(action, commitMode);
        if (href) {
          addRecentSearch(action);
          navigateFromSpotlight(navigate, href);
          appSearch.close();
          return;
        }

        drillAction(action);
      },
      [addRecentSearch, commitMode, drillAction, navigate],
    );

    const hoverAction = useCallback((index: number) => {
      hoveredIndexRef.current = index;
      setIgnoreHover(false);
    }, []);

    useEffect(() => {
      hoveredIndexRef.current = null;
      setIgnoreHover(false);
    }, [query, scopeKey]);

    const activateListAction = useCallback(() => {
      const { action, index, row } = resolveSpotlightAction(
        listRef.current,
        listedActions,
        hoveredIndexRef.current,
        ignoreHover,
      );

      if (row) {
        syncSpotlightSelection(listRef.current, row, index, (selected) => {
          appSearchStore.updateState((state) => ({ ...state, selected }));
        });
      }

      return action;
    }, [ignoreHover, listedActions]);

    useEffect(() => {
      const unsubs = [
        searchRegistry.register(navSearchProvider),
        ...PAGE_SEARCH_PROVIDERS.map((provider) =>
          searchRegistry.register(provider),
        ),
      ];

      return () => {
        unsubs.forEach((unsubscribe) => unsubscribe());
      };
    }, []);

    return (
      <>
        <AppSearchButton />
        <Spotlight.Root
          store={appSearchStore}
          shortcut={['mod + K']}
          query={query}
          onQueryChange={setQuery}
          maxHeight={400}
          shadow='none'
          overlayProps={{ backgroundOpacity: 0.45, blur: 0 }}
          transitionProps={{ transition: 'fade', duration: 100 }}
          styles={{
            content: { boxShadow: 'none', overflow: 'hidden' },
            action: { overflow: 'hidden' },
            actionBody: { minWidth: 0, overflow: 'hidden' },
            actionLabel: { overflow: 'hidden', textOverflow: 'ellipsis' },
            actionDescription: { overflow: 'hidden', textOverflow: 'ellipsis' },
          }}
          scrollAreaComponent={SpotlightScrollArea}
          closeOnActionTrigger={false}
        >
          <Spotlight.Search
            leftSection={<IconSearch size={20} />}
            rightSection={isLoading ? <Loader size={16} /> : undefined}
            placeholder={t('search.placeholder')}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                hoveredIndexRef.current = null;
                setIgnoreHover(true);
                return;
              }

              if (event.key === 'Enter') {
                activateListAction();
                return;
              }

              if (event.key !== 'Tab') {
                return;
              }

              event.preventDefault();

              if (event.shiftKey) {
                setQuery(popSearchQuery(query));
                return;
              }

              const action = activateListAction();
              if (action) {
                drillAction(action);
              }
            }}
          />
          <SpotlightResults
            actions={actions}
            recentActions={recentActions}
            listRef={listRef}
            scopeKey={scopeKey}
            isLoading={isLoading}
            commitMode={commitMode}
            onCommitModeChange={setCommitMode}
            onCommit={commitAction}
            onDrill={drillAction}
            onHover={hoverAction}
            onRemoveRecent={(action) => removeRecentSearch(action.id)}
            ignoreHover={ignoreHover}
          />
        </Spotlight.Root>
      </>
    );
  },
);